import React, { useState , useEffect, useRef, useCallback, useMemo } from 'react';
import { API } from "aws-amplify";
import { useHistory } from "react-router-dom";

import Fuse from 'fuse.js';
import swal from '@sweetalert/with-react';
import Dropdown from './Dropdown';

import '../css/school_data.css';
import '../css/select_school.css';

import { isEmpty, sortArrayOfObjects, useOutsideClick } from '../js/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faSchool, faCheckCircle, faEllipsisV, faSearch } from '@fortawesome/free-solid-svg-icons';

const schoolConnectionIsActive = (school_info) => `${school_info?.is_active ?? '1'}` !== '0';

export default function SelectSchools({ isAuth, authComplete, schoolType, setLoadScreen, userInfo, userInfoLoading, setUserInfo, setCurrentSchoolID, schoolInfo, schoolInfoLoading, setSchoolInfo, resetState, getSchoolData, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo }) {
	let history = useHistory();
	const ref = useRef();
	const schoolNameInputRef = useRef(null);
	const schoolNameSaveInFlightRef = useRef(false);
	const skipSchoolNameBlurSaveRef = useRef(false);
	
	const [isLoading, setIsLoading] = useState(true);
	
	const [showAdd, setShowAdd] = useState(false);
	
	const [addError, setAddError] = useState(null);
	const [updateError, setUpdateError] = useState(null);
	const [message, setMessage] = useState(null);
	const [newSchoolIDToScrollTo, setNewSchoolIDToScrollTo] = useState(null);
	
	const [sortedSchools, setSortedSchools] = useState([]);
	const [openSchools, setOpenSchools] = useState([]);
	const [editingSchoolID, setEditingSchoolID] = useState(null);
	const [schoolNameDraft, setSchoolNameDraft] = useState('');
	const [schoolNameSavingID, setSchoolNameSavingID] = useState(null);
	const [schoolView, setSchoolView] = useState('active');
	const [searchValue, setSearchValue] = useState('');
	const [schoolStatusUpdating, setSchoolStatusUpdating] = useState(null);

	const [newSchoolInfo, setNewSchoolInfo] = useState({school_name:null, school_type:null});
	
	const colors = ['turquoise', 'bright-blue', 'pastel-light-purple', 'yellow'];
	const getUniqueSchoolsById = useCallback((schools = []) => {
		const logged_school_ids = new Set();

		return schools.reduce((results, school_info) => {
			const school_id = school_info && school_info.school_id;
			if(!school_id || logged_school_ids.has(school_id)) return results;

			logged_school_ids.add(school_id);
			results.push(school_info);
			return results;
		}, []);
	}, []);
	const activeSchools = useMemo(() => sortedSchools.filter(schoolConnectionIsActive), [sortedSchools]);
	const inactiveSchools = useMemo(() => sortedSchools.filter(school_info => !schoolConnectionIsActive(school_info)), [sortedSchools]);
	const schoolsInCurrentView = (schoolView === 'inactive') ? inactiveSchools : activeSchools;
	const displayedSchools = useMemo(() => {
		const trimmed_search_value = searchValue.trim();
		if(!trimmed_search_value) return schoolsInCurrentView;

		const schools_fuse = new Fuse(schoolsInCurrentView, {
			keys: ['name'],
			threshold: .1
		});

		return schools_fuse.search(trimmed_search_value).map(result => result.item);
	}, [schoolsInCurrentView, searchValue]);

	useOutsideClick(ref, () => {
		const school_id = ref.current.closest('.select-school-container').getAttribute('data-schoolid');
		const school_index = openSchools.findIndex(school => school === school_id);

		if(school_index !== -1) openSchools.splice(school_index, 1);
		setOpenSchools([...openSchools]);
	});

	const toggleOpenSchool = (school_id) => {
		const school_index = openSchools.findIndex(school => school === school_id);
		
		if(school_index === -1)
		{
			openSchools.push(school_id);
		}
		else
		{
			openSchools.splice(school_index, 1);
		}
		
		setOpenSchools([...openSchools]);
	}
	
	const startEditingSchoolName = (school_info) => {
		if(!school_info || !schoolConnectionIsActive(school_info) || editingSchoolID || schoolNameSaveInFlightRef.current) return;

		setOpenSchools(openSchools.filter(school_id => school_id !== school_info.school_id));
		setEditingSchoolID(school_info.school_id);
		setSchoolNameDraft(school_info.name || '');
		setUpdateError(null);
	};

	const cancelEditingSchoolName = (skip_blur_save = false) => {
		if(schoolNameSaveInFlightRef.current) return;
		if(skip_blur_save)
		{
			skipSchoolNameBlurSaveRef.current = true;
			window.setTimeout(() => {
				skipSchoolNameBlurSaveRef.current = false;
			}, 0);
		}

		setEditingSchoolID(null);
		setSchoolNameDraft('');
		setUpdateError(null);
	};

	const handleSearch = (e) => {
		setSearchValue(e.target.value);
	};

	const changeSchoolView = (view) => {
		setSchoolView(view);
		setSearchValue('');
		setOpenSchools([]);
		setEditingSchoolID(null);
		setSchoolNameDraft('');
		setUpdateError(null);
		setShowAdd(false);
	};

	useEffect(() => {
		if(!editingSchoolID || schoolNameSavingID || !schoolNameInputRef.current) return;

		schoolNameInputRef.current.focus();
		schoolNameInputRef.current.select();
	}, [editingSchoolID, schoolNameSavingID]);
	
	///////////////////////////
	///// UPDATE FUNCTION /////
	///////////////////////////
	const createNewSchool = async (e) => {
		if(e.key !== 'Enter' && e.type !== 'click') return;
		setAddError(null);
			
		if(!newSchoolInfo.school_name)
		{
			setAddError('Your school needs to have a name!');
			
			// Erase message after 2 seconds
			setTimeout(function () {
				setAddError(null);
			}, 2000);
			return false;
		}

		if(!newSchoolInfo.school_type)
		{
			setAddError('Your school needs to be set as either a High School/Junior High School/Middle School or an Elementary School!');
			
			// Erase message after 2 seconds
			setTimeout(function () {
				setAddError(null);
			}, 2000);
			return false;
		}

		/// Show processing modal
		const options =  {
			title: "Processing...",
			buttons: false,
			closeOnClickOutside: false,
			content: (
				<div className='sweet-alert-dont-show-message'>
					<div className='loader'></div>
					<p>Please wait while we create your new school...</p>
				</div>
			)
		}
		
		swal(options);

		/// UPDATE BACKEND
		const passed_data = {update_type:'add', school_name:newSchoolInfo.school_name, school_type:newSchoolInfo.school_type};
	
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-school'
		const myInit = { // OPTIONAL
			response: true,
			body: JSON.stringify(passed_data),
		};
		
		try {
			const response = await API.post(apiName, url, myInit);
			const response_data = response.data;
			
			const new_school_id = response_data.school_id;
			userInfo.schools.push({school_id:new_school_id, name:newSchoolInfo.school_name, type:newSchoolInfo.school_type, is_active:'1'});
			
			swal.close();
			setShowAdd(false);
			setSchoolView('active');
			setSearchValue('');
			setNewSchoolIDToScrollTo(new_school_id);
			setMessage(response_data.message);
			setUserInfo({...userInfo});
			
			// Erase message after 2 seconds
			setTimeout(function () {
				setMessage(null);
			}, 2000);
		} catch(e)
		{
			console.log(e);
		}
	}

	const copySchool = async (school_id) => {
		const source_school = userInfo.schools.find(school => `${school.school_id}` === `${school_id}`);
		if(!source_school || !schoolConnectionIsActive(source_school)) return false;

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		
		try {
			const options_response = await API.get(apiName, '/get-schedule-info', {
				response: true,
				queryStringParameters: {school_id, copy_options: '1'},
			});
			const schedules = options_response.data?.schedules || [];
			if(schedules.length === 0) {
				await swal({icon: 'error', title: 'Cannot copy school', text: 'This school has no schedule in the current year.'});
				return false;
			}
			let selected_schedule_version_id = schedules[0].schedule_version_id;
			if(schedules.length > 1) {
				const schedule_options = schedules.map(schedule => ({
					value: schedule.schedule_version_id,
					display: `${schedule.schedule_name}${`${schedule.current_bool}` === '1' ? ' (Current)' : ''}`,
				}));
				const confirmed = await swal({
					title: 'Choose a schedule to copy',
					text: 'The new school will contain the selected schedule and its data.',
					className: 'copy-school-schedule-swal',
					buttons: {
						cancel: {text: 'Cancel', value: false, visible: true, className: 'gray-btn'},
						confirm: {text: 'Copy Schedule', value: true, visible: true, className: 'blue-btn'},
					},
					content: (
						<div className='copy-school-schedule-dropdown'>
							<Dropdown data={schedule_options} currentValue={selected_schedule_version_id} handleChange={new_value => { selected_schedule_version_id = new_value; }} />
						</div>
					)
				});
				if(!confirmed) return false;
			}

			swal({
				title: 'Copying school...',
				buttons: false,
				closeOnClickOutside: false,
				content: <div className='sweet-alert-dont-show-message'><div className='loader'></div><p>Please wait while we copy the selected schedule.</p></div>,
			});
			const response = await API.post(apiName, '/copy-school', {
				response: true,
				body: JSON.stringify({school_id, schedule_version_id: selected_schedule_version_id}),
			});
			const response_data = response.data;
			const status = response_data.status;

			if(status === 'error')
			{
				const failure_options =  {
					icon:"error",
					title: "Could not copy school",
					text: response_data.message,
					dangerMode: true,
				}

				swal(failure_options);
				return false;
			}

			const new_school_id = response_data.school_id;

			const old_school_name = userInfo.schools.find(school => school.school_id === school_id).name;
			const new_school_name = response_data.school_name || `${old_school_name} COPY`;

			userInfo.schools.push({school_id:new_school_id, name:new_school_name, is_active:'1'});
			setUserInfo({...userInfo});

			const success_options =  {
				icon:"success",
				title: "School succesfully copied!",
				text: `${new_school_name} has successfully been created`,
			}

			swal(success_options);
		} catch(e)
		{
			console.log(e);
			swal({icon: 'error', title: 'Could not copy school', text: 'The copy did not finish. Please try again or contact support.'});
		}
	}

	const deleteSchool = async (school_id) => {
		if(!school_id) return false;

		const school_info = userInfo.schools.find(school => school.school_id === school_id);
		const school_name = school_info.name;

		const options =  {
			title: "Are you sure?",
			text: `Do you really want to delete ${school_name}?`,
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div className='sweet-alert-dont-show-message'>
					<div style={{width:'330px', textAlign:'left', margin:'auto'}}>
						<p>Deleting this school will also delete any existing data at the school, including:</p>
						<ul>
							<li>Teachers</li>
							<li>Courses</li>
							<li>Classrooms</li>
							<li>Schedules</li>
						</ul>
						<p>Are you <span className='red-text'>absolutely sure</span> you want to delete {school_name}?</p>
					</div>
				</div>
			)
		}
		
		if(await swal(options))
		{
			const options =  {
				title: "Processing...",
				buttons: false,
				closeOnClickOutside: false,
				content: (
					<div className='sweet-alert-dont-show-message'>
						<div className='loader'></div>
						<p>Please wait while we delete this school...</p>
					</div>
				)
			}
			
			swal(options);

			const passed_data = {update_type:'delete', school_id:school_id};
		
			const apiName = process.env.REACT_APP_ENDPOINT_NAME;
			const url = '/admin/update-school'
		    const myInit = { // OPTIONAL
		        response: true,
		        body: JSON.stringify(passed_data),
		    };
		    
		    try {
				const response = await API.post(apiName, url, myInit);
				const response_data = response.data;
				
				const school_index = userInfo.schools.findIndex(school => school.school_id === school_id);
				if(school_index !== -1) userInfo.schools.splice(school_index, 1);

				const current_school_id = ('school_id' in schoolInfo) ? schoolInfo.school_id : null;
				
				// Check if any schools exist, if not reset state
				if(userInfo.schools.length === 0 || current_school_id === school_id) 
				{
					resetState();
				}
				else
				{
					setCurrentSchoolID(current_school_id);
				}
				
				swal.close();
				setShowAdd(false);
				setMessage(response_data.message);
				setUserInfo({...userInfo});
				
				// Erase message after 2 seconds
				setTimeout(function () {
			       setMessage(null);
			    }, 2000);
			} catch(e)
			{
				console.log(e);
			}
		}
	}

	const updateSchoolName = async (school_id) => {
		if(!school_id || `${editingSchoolID}` !== `${school_id}` || schoolNameSaveInFlightRef.current) return;

		const school_info = userInfo.schools.find(school => `${school.school_id}` === `${school_id}`);
		if(!school_info || !schoolConnectionIsActive(school_info)) return;

		const school_name = schoolNameDraft.trim();
		if(!school_name)
		{
			setUpdateError('Your school needs to have a name!');
			window.setTimeout(() => schoolNameInputRef.current?.focus(), 0);
			return;
		}

		if(school_name === `${school_info.name || ''}`.trim())
		{
			cancelEditingSchoolName();
			return;
		}

		schoolNameSaveInFlightRef.current = true;
		setSchoolNameSavingID(school_id);
		setUpdateError(null);

		const passed_data = {update_type:'update', school_id:school_id, school_name:school_name};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-school';
		const myInit = {
			response: true,
			body: JSON.stringify(passed_data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const response_data = response.data;
			const updated_schools = userInfo.schools.map(school => (
				`${school.school_id}` === `${school_id}` ? {...school, name:school_name} : school
			));
			const current_school_id = ('school_id' in schoolInfo) ? schoolInfo.school_id : null;

			if(`${current_school_id}` === `${school_id}`) setSchoolInfo({...schoolInfo, name:school_name});

			setUserInfo({...userInfo, schools:updated_schools});
			setEditingSchoolID(null);
			setSchoolNameDraft('');
			setMessage(response_data.message);

			setTimeout(function () {
				setMessage(null);
			}, 2000);
		} catch(e)
		{
			console.log(e);
			setUpdateError('The school name could not be saved. Please try again.');
		} finally {
			schoolNameSaveInFlightRef.current = false;
			setSchoolNameSavingID(null);
		}
	};

	const handleSchoolNameKeyDown = (e, school_id) => {
		if(e.key === 'Enter')
		{
			e.preventDefault();
			updateSchoolName(school_id);
		}
		else if(e.key === 'Escape')
		{
			e.preventDefault();
			cancelEditingSchoolName(true);
		}
	};

	const handleSchoolNameBlur = (school_id) => {
		if(skipSchoolNameBlurSaveRef.current)
		{
			skipSchoolNameBlurSaveRef.current = false;
			return;
		}

		window.setTimeout(() => updateSchoolName(school_id), 0);
	};

	const setSchoolConnectionActive = async (school_id, is_active) => {
		if(!school_id || schoolStatusUpdating) return;

		const school_info = userInfo.schools.find(school => school.school_id === school_id);
		if(!school_info) return;

		if(!is_active)
		{
			const confirmed = await swal({
				title: 'Move school to Inactive?',
				text: `${school_info.name} will be hidden from the Active school list. No school data will be deleted.`,
				icon: 'warning',
				buttons: {
					cancel: {
						text: 'Cancel',
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: 'Make Inactive',
						value: true,
						visible: true,
						className: 'red-btn'
					},
				}
			});

			if(!confirmed) return;
		}

		setSchoolStatusUpdating(school_id);
		swal({
			title: 'Processing...',
			buttons: false,
			closeOnClickOutside: false,
			content: (
				<div className='sweet-alert-dont-show-message'>
					<div className='loader'></div>
					<p>Please wait while we update this school...</p>
				</div>
			)
		});

		const passed_data = {update_type:'set_connection_active', school_id:school_id, is_active:(is_active ? '1' : '0')};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-school';
		const myInit = {
			response: true,
			body: JSON.stringify(passed_data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const response_data = response.data;

			if(response_data.status === 'error')
			{
				await swal({icon:'error', title:'School was not updated', text:response_data.message});
				return;
			}

			const updated_schools = userInfo.schools.map(school => {
				if(school.school_id !== school_id) return school;
				return {...school, is_active:(is_active ? '1' : '0')};
			});
			const current_school_id = ('school_id' in schoolInfo) ? schoolInfo.school_id : null;

			if(!is_active && current_school_id === school_id) resetState();

			changeSchoolView(is_active ? 'active' : 'inactive');
			setNewSchoolIDToScrollTo(school_id);
			setUserInfo({...userInfo, schools:updated_schools});
			setMessage(response_data.message);
			swal.close();

			setTimeout(function () {
				setMessage(null);
			}, 2000);
		} catch(e)
		{
			console.log(e);
			await swal({icon:'error', title:'School was not updated', text:'Please try again.'});
		} finally {
			setSchoolStatusUpdating(null);
		}
	}

	const selectSchool = useCallback((school_id) => {
		const selected_school = Array.isArray(userInfo.schools) ? userInfo.schools.find(school => school.school_id === school_id) : null;
		if(!selected_school || !schoolConnectionIsActive(selected_school)) return;

		const current_school_id = ('school_id' in schoolInfo) ? schoolInfo.school_id : null;
		if(current_school_id === school_id)
		{
			if(userInfo.role_id === '4')
			{
				setLoadScreen('magnetboard');
				history.push(`/app/${schoolType}/magnetboard`);
			}
			else
			{
				setLoadScreen('progress');
				history.push(`/app/${schoolType}/progress`);
			}
			
			return;
		}

		sessionStorage.clear();
		 
		const school_params = {school_id:school_id, user_role_id:userInfo.role_id, schedule_version_id:null};
		getSchoolData(school_params, (school_info_data) => {
			const school_type = (school_info_data.type === '1') ? 'hsms' : ((school_info_data.type === '2') ? 'elementary' : null);
			const load_screen = (school_params.user_role_id === '4' || school_info_data.type === '2') ? 'magnetboard' : 'progress';

			setLoadScreen(load_screen);
			history.push(`/app/${school_type}/${load_screen}`);
		});
	}, [schoolInfo, userInfo, setLoadScreen, history, schoolType, getSchoolData]);

	//////////////////////////////
	///// DO ON STATE CHANGE /////
	//////////////////////////////
	useEffect(() => {
		// Check if user is logged in
		// If not, send them to log in screen with a redirect to this screen
		// If yes, check if user has permission
		// If not permission, redirect to loading page
		if(authComplete && !isAuth)
		{
			history.push({
				pathname: '/app/login',
				state: {redirect: '/app/select_school'}
			});
		}
		else if(authComplete && !userInfoLoading && !schoolInfoLoading)
		{
			setIsLoading(false);
		}
	}, [isAuth, authComplete, userInfoLoading, schoolInfoLoading, history]);

	useEffect(() => {
		setScreenAllowsUniversalSidebar(false);
		setUniversalSidebarLoadingInfo(null);
	}, [setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo]);

	useEffect(() => {
		const sorted_schools = (!isEmpty(userInfo)) ? sortArrayOfObjects(userInfo.schools, 'name', 'text', 'asc') : [];
		setSortedSchools([...getUniqueSchoolsById(sorted_schools)]);
	}, [userInfo, getUniqueSchoolsById]);

	useEffect(() => {
		if(!authComplete || !isAuth || userInfoLoading || schoolInfoLoading) return;

		const user_schools = (!isEmpty(userInfo) && Array.isArray(userInfo.schools)) ? getUniqueSchoolsById(userInfo.schools) : [];
		if(user_schools.length !== 1 || !schoolConnectionIsActive(user_schools[0])) return;

		selectSchool(user_schools[0].school_id);
	}, [authComplete, isAuth, userInfoLoading, schoolInfoLoading, userInfo, getUniqueSchoolsById, selectSchool]);

	useEffect(() => {
		if(!newSchoolIDToScrollTo) return;

		const scrollToNewSchool = () => {
			const new_school_div = document.querySelector(`[data-schoolid="${newSchoolIDToScrollTo}"]`);

			if(!new_school_div) return;

			new_school_div.scrollIntoView({ behavior: 'smooth', block: 'center' });
			setNewSchoolIDToScrollTo(null);
		};

		const timeout_id = window.setTimeout(scrollToNewSchool, 0);

		return () => window.clearTimeout(timeout_id);
	}, [sortedSchools, newSchoolIDToScrollTo]);
	
	const current_school_id = (schoolInfo && 'school_id' in schoolInfo) ? schoolInfo.school_id : null;
	const user_role_id = (!isEmpty(userInfo)) ? userInfo.role_id : null;
	const user_schools = (!isEmpty(userInfo) && Array.isArray(userInfo.schools)) ? getUniqueSchoolsById(userInfo.schools) : [];
	const show_add_new_schools = ((user_role_id === '1' || user_role_id === '2') || (user_role_id === '5' && user_schools.length === 0)) ? true : false;
	//console.log({newSchoolInfo});
	const isAdmin = user_role_id === '2';
	const isSuperAdmin = (!isEmpty(userInfo)) ? (userInfo.email === "contact@edario.com" || userInfo.email === "jhelman90@gmail.com") : false;
	const canChangeSchoolStatus = isAdmin || isSuperAdmin;

	return (
		<>
		<div id='select-school-container'>
			{isLoading ?
				(
					<div className='center-text'>
						<img src={require('../images/balls.gif')} alt='Loading...' style={{height:'40px'}}/>
					</div>
				):
				(
					<>
						<div className='select-school-header-row'>
							<div className='select-school-header-copy'>
								<h1 className='subscreen-main-header'>Select a School</h1>
								<p className='subscreen-subheader dark-blue-text'>Which school would you like to work on?</p>
							</div>
						</div>
						<div className='select-school-view-tabs' role='group' aria-label='School status'>
							<button type='button' aria-pressed={schoolView === 'active'} className={`select-school-view-tab ${schoolView === 'active' ? 'selected' : ''}`} onClick={() => changeSchoolView('active')}>Active <span>{activeSchools.length}</span></button>
							<button type='button' aria-pressed={schoolView === 'inactive'} className={`select-school-view-tab ${schoolView === 'inactive' ? 'selected' : ''}`} onClick={() => changeSchoolView('inactive')}>Inactive <span>{inactiveSchools.length}</span></button>
						</div>
					{schoolsInCurrentView.length >= 5 &&
						<div className='search-add-adding-input-container'>
							<input id='select-school-search-bar' className='search-add-adding-input' value={searchValue} onChange={handleSearch} placeholder={`Search ${schoolView} schools...`}/>
							<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch} />
						</div>
					}
					{displayedSchools.length === 0 &&
						<div id='select-school-no-schools-container'>
							<FontAwesomeIcon className='gray-text' style={{fontSize:'160px'}} icon={faSchool}/>
							<p className='gray-text'>{searchValue.trim() ? `No ${schoolView} schools match your search` : `No ${schoolView} schools`}</p>
						</div>
					}
					<div id='select-school-list'>
						{displayedSchools.map((school_info, index) => {
							const school_id = school_info.school_id;
							const name = school_info.name;
							const school_type = school_info.type;
							const is_active = schoolConnectionIsActive(school_info);
							
							const color_index = index % colors.length;
							const color = colors[color_index];
							const selected = is_active && (school_id === current_school_id);
							const school_open = openSchools.includes(school_id);
							const school_editing = `${editingSchoolID}` === `${school_id}`;
							const school_name_saving = `${schoolNameSavingID}` === `${school_id}`;
							const can_edit_school_name = isSuperAdmin && is_active && !editingSchoolID;
							
							return (
								<div className={`select-school-container parent-div click-restricted ${!is_active ? 'inactive' : ''} ${selected ? `${color}-border border-2` : ''}`} key={school_id} data-schoolid={school_id}>
									<div className='select-school-inner-container'>
										<FontAwesomeIcon className={`${color}-text select-school-icon`} icon={faSchool}/>
									<div className='select-school-name-column'>
										{school_editing ?
											<>
												<div className='school-data-inline-editor-container select-school-inline-editor'>
													{school_name_saving ?
														<div className='school-data-inline-saving-cell'>
															<img src={require('../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
														</div>
														:
														<input
															ref={schoolNameInputRef}
															className={`school-data-inline-edit-input select-school-inline-edit-input ${updateError ? 'error' : ''}`}
															value={schoolNameDraft}
															onChange={(e) => setSchoolNameDraft(e.target.value)}
															onKeyDown={(e) => handleSchoolNameKeyDown(e, school_id)}
															onBlur={() => handleSchoolNameBlur(school_id)}
														/>
													}
												</div>
												{updateError && <div className='error-message-no-box select-school-inline-edit-error'>{updateError}</div>}
											</>
											:
											<h2 className={`dark-blue-text select-school-name ${can_edit_school_name ? 'school-data-inline-editable-cell' : ''}`} title={can_edit_school_name ? 'Click to edit' : undefined} onClick={can_edit_school_name ? () => startEditingSchoolName(school_info) : undefined}>{name}</h2>
										}
										{(school_type === '1') ?
										(
											<div className='dark-gray-text small-text select-school-school-type'>High/Junior/Middle School</div>
										):(school_type === '2') ?
										(
											<div className='dark-gray-text small-text select-school-school-type'>Elementary School</div>
										):null}
									</div>
										{is_active ?
										<div className={`btn ${color}`} onClick={() => selectSchool(school_id)}>{selected ? 'Continue Working' : 'Select'}</div>
										:
										<div className='select-school-inactive-label'>Inactive</div>
										}
										{canChangeSchoolStatus &&
										<div className='select-school-options-column'>
											<button type='button' className='select-school-more-options-btn gray-to-dark-blue-link click-restricted' aria-label={`Open options for ${name}`} aria-expanded={school_open} onClick={() => toggleOpenSchool(school_id)}>
												<FontAwesomeIcon className='select-school-more-options' icon={faEllipsisV}/>
											</button>
											<div className='select-school-more-options-container'>
												{school_open &&
													<div className='data-options-container' ref={ref}>
														<div className='data-options-option' onClick={() => setSchoolConnectionActive(school_id, !is_active)}>{is_active ? 'Make Inactive' : 'Make Active'}</div>
														{isSuperAdmin && is_active && <div className='data-options-option' onClick={() => startEditingSchoolName(school_info)}>Change School Name</div>}
														{isSuperAdmin && is_active && <div className='data-options-option' onClick={() => copySchool(school_id)}>Copy School</div>}
														{isSuperAdmin && <div className='data-options-option' onClick={(e) => deleteSchool(school_id)}>Delete School</div>}
													</div>
												}
											</div>
										</div>
										}
									</div>
									{selected &&
										<FontAwesomeIcon className={`school-selected-check ${color}-text`} icon={faCheckCircle}/>
									}
								</div>
							)
						})}
					</div>
					{message &&
						<div className='success-message-no-box' style={{marginTop:'15px'}}>{message}</div>
					}
					{show_add_new_schools && schoolView === 'active' &&
						<>
						{showAdd &&
							<div id="add-new-school-container">
								<div>
									<div className='assign-resources-adding-label'>School Name</div>
									<input id='new-school-name' className={`edit-department-input ${addError ? 'error' : ''}`} onKeyDown={(e) => createNewSchool(e)} onChange={(e) => setNewSchoolInfo({...newSchoolInfo, ...{school_name:e.target.value}})}/>
									<div className='assign-resources-adding-label' style={{marginTop:'15px'}}>School Type</div>
									<div className='text-align-left'>
										<div className={`option-btn medium-text ${newSchoolInfo.school_type === '1' && 'option-btn-selected'}`} onClick={() => setNewSchoolInfo({...newSchoolInfo, ...{school_type:'1'}})}>High School, Junior High School, or Middle School</div>
										<div className={`option-btn medium-text ${newSchoolInfo.school_type === '2' && 'option-btn-selected'}`} onClick={() => setNewSchoolInfo({...newSchoolInfo, ...{school_type:'2'}})}>Elementary School</div>
									</div>
									{addError && 
										<div className='error-message-no-box'>{addError}</div>
									}
								</div>
								<div id='add-new-school-btn-container'>
									<div className='btn gray-to-red-btn select-school-add-new-btn'  onClick={() => setShowAdd(!showAdd)}>
										<FontAwesomeIcon icon={faTimes}/>
									</div>
									<div className='btn gray-to-green-btn select-school-add-new-btn' onClick={(e) => createNewSchool(e)}>
										<FontAwesomeIcon  icon={faCheck}/>
									</div>
								</div>
							</div>
						}
						{(!showAdd && isSuperAdmin) &&
							<div className='add-assign-resources-row' onClick={() => setShowAdd(!showAdd)}>Add New School</div>
						}
						</>
					}
					</>
				)
			}
		</div>
		</>
	);
}
